import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { MenuConstants } from "./../../infrastructure/MenuConstants";

import { AuthService } from "../../services/auth.service";
import { Roles } from "./auth.role";

@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate, CanActivateChild {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    const url: string = state.url;

    return this.checkLogin(url);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return this.canActivate(route, state);
  }

  checkLogin(url: string): boolean {
    if (
      url.includes(MenuConstants.Status) ||
      url.includes(MenuConstants.Todos)
    ) {
      return this.canViewMonitor();
    }

    if (
      url.includes(MenuConstants.MonitorRecebiveis) ||
      url.includes(MenuConstants.MonitorArquivos)
    ) {
      return this.canViewReceivable();
    }

    if (url.includes(MenuConstants.Tickets.Quota)) {
      return this.canViewTicketQuota();
    }

    if (url.includes(MenuConstants.Tickets.PrivateFixedIncome)) {
      return this.canViewTicketPrivateFixedIncome();
    }

    if (url.includes(MenuConstants.Tickets.PublicFixedIncome)) {
      return this.canViewTicketPublicFixedIncome();
    }

    if (url.includes(MenuConstants.Tickets.Futures)) {
      return this.canViewTicketFutures();
    }

    if (url.includes(MenuConstants.Tickets.Margin)) {
      return this.canViewTicketMargin();
    }

    if (url.includes(MenuConstants.Tickets.Contracted)) {
      return this.canViewTicketContracted();
    }

    if (url.includes(MenuConstants.Tickets.CurrencyTerm)) {
      return this.canViewTicketCurrencyTerm();
    }

    if (url.includes(MenuConstants.Tickets.VariableIncome)) {
      return this.canViewTicketVariableIncome();
    }

    if (url.includes(MenuConstants.Tickets.SwapCetip)) {
      return this.canViewTicketSwapCetip();
    }

    if (url.includes(MenuConstants.Tickets.Transfer)) {
      return this.canViewTicketTransfer();
    }

    if (url.includes(MenuConstants.Tickets.Loan)) {
      return this.canViewTicketLoan();
    }

    if (url.includes(MenuConstants.Tickets.Exchange)) {
      return this.canViewTicketExchange();
    }

    if (url.includes(MenuConstants.Tickets.Expenses)) {
      return this.canViewTicketExpenses();
    }

    if (url.includes(MenuConstants.Tickets.Liabilities)) {
      return this.canViewTicketLiabilities();
    }

    if (url.includes(MenuConstants.Tickets.StructuredOperations)) {
      return this.canViewTicketOperationStructured();
    }

    if (url.includes(MenuConstants.Tickets.StructuredOperationOrder)) {
      return this.canViewTicketOperationStructured();
    }

    if (url.includes(MenuConstants.UploadsPesc)) {
      return this.canViewTicketVariableIncome();
    }

    if (url.includes(MenuConstants.UploadsTicket)) {
      return this.canCreateTicket();
    }

    if (url.includes(MenuConstants.UploadsCarga)) {
      return this.canCreateLoad();
    }

    if (url.includes(MenuConstants.ObterCargas)) {
      return this.canCreateLoad();
    }

    if (url.includes(MenuConstants.Usuarios)) {
      return this.canViewUsers();
    }

    if (url.includes(MenuConstants.Grupos)) {
      return this.canViewGroups();
    }

    if (url.includes(MenuConstants.Cutoff)) {
      return this.canViewCutoff();
    }

    if (url.includes(MenuConstants.Calendario)) {
      return this.canCreateLoad();
    }

    if (url.includes(MenuConstants.Counterpart)) {
      return this.canCreateCounterpart();
    }

    if (this.authService.isLoggedIn()) {
      return true;
    }

    // Store the attempted URL for redirecting
    this.authService.redirectUrl = url;

    // Navigate to the login page with extras
    this.router.navigate(["/login"]);
    return false;
  }

  canViewMonitor() {
    const roles = [
      Roles.ViewMonitorFundQuota,
      Roles.ViewMonitorPrivateFixedIncome,
      Roles.ViewMonitorPublicFixedIncome,
      Roles.ViewMonitorFutures,
      Roles.ViewMonitorMargin,
      Roles.ViewMonitorContracted,
      Roles.ViewMonitorCurrencyTerm,
      Roles.ViewMonitorVariableIncome,
      Roles.ViewMonitorSwapCetip,
      Roles.ViewMonitorExchange,
      Roles.ViewReceivable,
      Roles.ViewLiabilitiesMonitor,
      Roles.ViewExpensesMonitor,
      Roles.ViewMonitorOperationStructured,
      Roles.ViewStructuredOperationOrderMonitor,
    ];

    return this.authService.hasRoles(roles);
  }

  canViewReceivable() {
    const roles = [Roles.ViewReceivable];
    return this.authService.hasRoles(roles);
  }

  canViewTicketQuota() {
    const roles = [Roles.CreateTicketFundQuota, Roles.ViewMonitorFundQuota];
    return this.authService.hasRoles(roles);
  }

  canViewTicketPrivateFixedIncome() {
    const roles = [
      Roles.CreateTicketPrivateFixedIncome,
      Roles.ViewMonitorPrivateFixedIncome,
    ];
    return this.authService.hasRoles(roles);
  }

  canViewTicketPublicFixedIncome() {
    const roles = [
      Roles.CreateTicketPublicFixedIncome,
      Roles.ViewMonitorPublicFixedIncome,
    ];
    return this.authService.hasRoles(roles);
  }

  canViewTicketFutures() {
    const roles = [Roles.CreateTicketFutures, Roles.ViewMonitorFutures];
    return this.authService.hasRoles(roles);
  }

  canViewTicketMargin() {
    const roles = [Roles.CreateTicketMargin, Roles.ViewMonitorMargin];
    return this.authService.hasRoles(roles);
  }

  canViewTicketContracted() {
    const roles = [Roles.CreateTicketContracted, Roles.ViewMonitorContracted];
    return this.authService.hasRoles(roles);
  }

  canViewTicketCurrencyTerm() {
    const roles = [
      Roles.CreateTicketCurrencyTerm,
      Roles.ViewMonitorCurrencyTerm,
    ];
    return this.authService.hasRoles(roles);
  }

  canViewTicketVariableIncome() {
    const roles = [
      Roles.CreateTicketVariableIncome,
      Roles.ViewMonitorVariableIncome,
    ];
    return this.authService.hasRoles(roles);
  }

  canViewTicketSwapCetip() {
    const roles = [Roles.CreateTicketSwapCetip, Roles.ViewMonitorSwapCetip];
    return this.authService.hasRoles(roles);
  }

  canViewTicketTransfer() {
    const roles = [Roles.CreateTicketTransfer, Roles.ViewMonitorTransfer];
    return this.authService.hasRoles(roles);
  }

  canViewTicketLoan() {
    const roles = [Roles.CreateTicketLoan, Roles.ViewMonitorLoan];
    return this.authService.hasRoles(roles);
  }

  canViewTicketExchange() {
    const roles = [Roles.CreateTicketExchange, Roles.ViewMonitorExchange];
    return this.authService.hasRoles(roles);
  }

  canViewTicketExpenses() {
    const roles = [Roles.CreateExpensesTicket, Roles.ViewExpensesMonitor];
    return this.authService.hasRoles(roles);
  }

  canViewTicketLiabilities() {
    const roles = [Roles.CreateLiabilitiesTicket, Roles.ViewLiabilitiesMonitor];
    return this.authService.hasRoles(roles);
  }

  canViewTicketOperationStructured() {
    const roles = [
      Roles.CreateTicketStructuredOperations,
      Roles.ViewMonitorOperationStructured,
    ];
    return this.authService.hasRoles(roles);
  }

  canCreateTicket() {
    const roles = [Roles.GestorCreateTicket];
    return this.authService.hasRoles(roles);
  }

  canCreateLoad() {
    const roles = [Roles.AdminCreateLoad];
    return this.authService.hasRoles(roles);
  }

  canViewUsers() {
    const roles = [Roles.CreateMasterUser, Roles.CreateSubordinateUser];
    return this.authService.hasRoles(roles);
  }

  canViewGroups() {
    const roles = [Roles.CreateMasterUser];
    return this.authService.hasRoles(roles);
  }

  canViewCutoff() {
    const roles = [Roles.ChangeDefaultLimitTime, Roles.ChangeLimitTime];
    return this.authService.hasRoles(roles);
  }

  canCreateCounterpart() {
    const roles = [Roles.CreateCounterpart];
    return this.authService.hasRoles(roles);
  }
}
